import { useEffect } from "react";

interface PageMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: string;
  noindex?: boolean;
  structuredData?: Record<string, any>;
  publishedTime?: string;
  /**
   * Canonical path to use instead of the current pathname. Set this on pages that
   * support query-string variants (filters, sort, pagination) so every variant
   * points crawlers at the same canonical URL and doesn't compete for rankings.
   */
  canonicalPath?: string;
  /**
   * When true, pages whose URL has a query string are marked noindex,follow —
   * users can still visit them but Google won't index each filter combo.
   */
  noindexIfQuery?: boolean;
}

const SITE_NAME = "Of Course It's Good";
const BASE_URL = "https://ofcourseitsgood.com";

const getOrCreateMeta = (attr: string, value: string): HTMLMetaElement => {
  let el = document.querySelector(`meta[${attr}="${value}"]`) as HTMLMetaElement;
  if (!el) {
    el = document.createElement("meta");
    const [a, v] = attr === "property" ? ["property", value] : ["name", value];
    el.setAttribute(a, v);
    document.head.appendChild(el);
  }
  return el;
};

const getOrCreateLink = (rel: string): HTMLLinkElement => {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  return el;
};

const PageMeta = ({ title, description, ogImage, ogType = "website", noindex, structuredData, publishedTime, canonicalPath, noindexIfQuery }: PageMetaProps) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const path = canonicalPath ?? window.location.pathname;
    const url = `${BASE_URL}${path}`;
    const hasQuery = typeof window !== "undefined" && window.location.search.length > 1;
    const shouldNoindex = noindex || (noindexIfQuery && hasQuery);

    const tags: { attr: string; key: string; content: string }[] = [
      { attr: "name", key: "description", content: description },
      { attr: "property", key: "og:title", content: title },
      { attr: "property", key: "og:description", content: description },
      { attr: "property", key: "og:type", content: ogType },
      { attr: "property", key: "og:url", content: url },
      { attr: "property", key: "og:site_name", content: SITE_NAME },
      { attr: "name", key: "twitter:card", content: ogImage ? "summary_large_image" : "summary" },
      { attr: "name", key: "twitter:title", content: title },
      { attr: "name", key: "twitter:description", content: description },
    ];

    if (ogImage) {
      tags.push({ attr: "property", key: "og:image", content: ogImage });
      tags.push({ attr: "name", key: "twitter:image", content: ogImage });
    }

    if (shouldNoindex) {
      tags.push({ attr: "name", key: "robots", content: "noindex, follow" });
    }

    if (publishedTime) {
      tags.push({ attr: "property", key: "article:published_time", content: publishedTime });
      tags.push({ attr: "property", key: "article:author", content: "Jessica Secrest" });
    }

    const elements = tags.map(({ attr, key, content }) => {
      const el = getOrCreateMeta(attr, key);
      el.content = content;
      return el;
    });

    const canonical = getOrCreateLink("canonical");
    canonical.href = url;

    // Strip any pre-existing hreflang tags — single-language site, hreflang was
    // creating "conflict" flags on query-string URLs (Semrush audit).
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    // JSON-LD structured data
    let jsonLdScript: HTMLScriptElement | null = null;
    if (structuredData) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(jsonLdScript);
    }

    return () => {
      document.title = prevTitle;
      elements.forEach((el) => el.remove());
      canonical.remove();
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, ogImage, ogType, noindex, structuredData, publishedTime, canonicalPath, noindexIfQuery]);

  return null;
};

export default PageMeta;
